import { MicrophoneAccessType } from '@modules/BrowserModule/types';
import type { DeviceFailureResult, ErrorCategory } from '../../types';
import { audioDeviceStrings } from '../audioDeviceStrings';

/**
 * Merges the softphone's own errorCategory with browser-level microphone
 * permission state so the panel can display the correct error UI even when
 * the softphone never initialized (e.g. browser denied mic access).
 */
export function deriveEffectiveErrorCategory(
  errorCategory: ErrorCategory | null,
  isDevicesReady: boolean,
  microphoneAccess: MicrophoneAccessType,
): ErrorCategory | null {
  if (errorCategory) return errorCategory;
  if (isDevicesReady) return null;
  if (microphoneAccess === MicrophoneAccessType.PERMISSION_DENIED)
    return 'permission_denied';
  if (microphoneAccess === MicrophoneAccessType.ERROR) return 'other';
  return null;
}

/**
 * Returns the placeholder text for the microphone combobox when initialization
 * failed — "No microphone found" for permission issues, "No devices available"
 * when no input devices were enumerated.
 */
export function getMicPlaceholder(
  micInitFailed: boolean,
  micPermissionDenied: boolean,
  inputDeviceCount: number,
): string | undefined {
  if (!micInitFailed) return undefined;
  if (micPermissionDenied)
    return audioDeviceStrings.placeholders.noMicrophoneFound;
  if (inputDeviceCount === 0)
    return audioDeviceStrings.placeholders.noDevicesAvailable;
  return undefined;
}

/**
 * Returns the placeholder text for the speaker combobox. When mic permission
 * is denied speakers are also unavailable ("No speaker detected"); otherwise
 * falls back to "No devices available" if no output devices were enumerated.
 */
export function getSpeakerPlaceholder(
  micPermissionDenied: boolean,
  speakerInitFailed: boolean,
  outputDeviceCount: number,
): string | undefined {
  if (micPermissionDenied)
    return audioDeviceStrings.placeholders.noSpeakerDetected;
  if (speakerInitFailed && outputDeviceCount === 0)
    return audioDeviceStrings.placeholders.noDevicesAvailable;
  return undefined;
}

/**
 * Returns a user-facing error string when a device switch or rollback failed,
 * or null if the device change succeeded.
 */
export function switchFailureMessage(
  result: DeviceFailureResult,
): string | null {
  if (
    result.status === 'switch_failed' ||
    result.status === 'rollback_failed'
  ) {
    return audioDeviceStrings.errors.deviceSwitchGeneric;
  }
  return null;
}

interface SwitchErrors {
  microphoneSwitchError: string | null;
  speakerSwitchError: string | null;
  ringOutputSwitchError: string | null;
}

const NO_SWITCH_ERRORS: SwitchErrors = {
  microphoneSwitchError: null,
  speakerSwitchError: null,
  ringOutputSwitchError: null,
};

/**
 * Derives switch error messages for all three audio channels from the
 * last device-change result. Returns nulls when devices are not ready
 * or no error has been recorded.
 */
export function deriveSwitchErrors(
  isDevicesReady: boolean,
  lastSetDevicesError: {
    audioInput: DeviceFailureResult;
    audioOutput: DeviceFailureResult;
    ringOutput: DeviceFailureResult;
  } | null,
): SwitchErrors {
  if (!isDevicesReady || !lastSetDevicesError) return NO_SWITCH_ERRORS;
  return {
    microphoneSwitchError: switchFailureMessage(lastSetDevicesError.audioInput),
    speakerSwitchError: switchFailureMessage(lastSetDevicesError.audioOutput),
    ringOutputSwitchError: switchFailureMessage(lastSetDevicesError.ringOutput),
  };
}
